import { useCallback, useEffect, useRef, useState } from "react";

// Start at the CSS contain size so the complete image/canvas is visible.
export const DEFAULT_IMAGE_ZOOM = 1;
export const MIN_IMAGE_ZOOM = 0.5;
export const MAX_IMAGE_ZOOM = 5;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Keeps image zoom and pan behavior in one place. Positions are viewport pixels,
 * which makes both button zooming and cursor-anchored wheel zooming predictable.
 */
export default function useImageZoom({
  viewportRef,
  defaultScale = DEFAULT_IMAGE_ZOOM,
  minScale = MIN_IMAGE_ZOOM,
  maxScale = MAX_IMAGE_ZOOM,
  zoomStep = 0.2,
} = {}) {
  const [transform, setTransform] = useState({
    scale: defaultScale,
    x: 0,
    y: 0,
  });
  const dragRef = useRef(null);

  const zoomAt = useCallback(
    (getRequestedScale, anchor) => {
      setTransform((current) => {
        const requestedScale = getRequestedScale(current.scale);
        const nextScale = clamp(requestedScale, minScale, maxScale);
        if (nextScale === current.scale) return current;

        const zoomAnchor =
          typeof anchor === "function" ? anchor(current) : anchor;
        const ratio = nextScale / current.scale;
        return {
          scale: nextScale,
          x: zoomAnchor.x - ratio * (zoomAnchor.x - current.x),
          y: zoomAnchor.y - ratio * (zoomAnchor.y - current.y),
        };
      });
    },
    [maxScale, minScale]
  );

  const zoomFromCenter = useCallback(
    (scaleDelta) => {
      if (!viewportRef?.current) return;
      zoomAt(
        (currentScale) => currentScale + scaleDelta,
        (current) => ({ x: current.x, y: current.y })
      );
    },
    [viewportRef, zoomAt]
  );

  const zoomIn = useCallback(
    () => zoomFromCenter(zoomStep),
    [zoomFromCenter, zoomStep]
  );
  const zoomOut = useCallback(
    () => zoomFromCenter(-zoomStep),
    [zoomFromCenter, zoomStep]
  );

  const resetZoom = useCallback(() => {
    setTransform({ scale: defaultScale, x: 0, y: 0 });
  }, [defaultScale]);

  useEffect(() => {
    const viewport = viewportRef?.current;
    if (!viewport) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();

      const rect = viewport.getBoundingClientRect();
      const anchor = {
        x: event.clientX - rect.left - rect.width / 2,
        y: event.clientY - rect.top - rect.height / 2,
      };

      const wheelFactor = Math.exp(-event.deltaY * 0.0015);
      zoomAt((currentScale) => currentScale * wheelFactor, anchor);
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [viewportRef, zoomAt]);

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0) return;

    dragRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }, []);

  const onPointerMove = useCallback((event) => {
    const previous = dragRef.current;
    if (!previous) return;

    const deltaX = event.clientX - previous.x;
    const deltaY = event.clientY - previous.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setTransform((current) => ({
      ...current,
      x: current.x + deltaX,
      y: current.y + deltaY,
    }));
  }, []);

  const onPointerUp = useCallback((event) => {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  return {
    transform,
    transformStyle: {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
      transformOrigin: "center center",
    },
    zoomIn,
    zoomOut,
    resetZoom,
    viewportHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
